import { Immutable } from "immer";
import React from "react";
import { UGCGrid as UGCPostGrid } from "tsi-common-react/src/components/UGCGrid/index";
import { isoImageURL } from "tsi-common-react/src/models/nominals";
import type {
    UGCPost as IUGCPost,
    UGCListSubBlock as IUGCTile,
} from "tsi-common-react/src/models/ugc";
import { t } from "ttag";

import type { UGCGrid as Value } from "@reactivated";

type IProps = Immutable<{
    value: Value;
}>;

type ITileItem = NonNullable<Value["posts"]>[number];
type IPost = NonNullable<ITileItem["post"]>;

/**
 * Convert one reactivated-serialized UGC post into the io-ts shape consumed
 * by the shared grid component (`tsi-common-react/src/components/UGCGrid`). The
 * wire data is byte-identical to the legacy `streamfield_client_render_json`
 * payload that the old `UGCGridTemplate` blind-cast after `JSON.parse`; this
 * mapping only reconciles the type-level differences (branded `ImageURL`
 * strings, and `null` vs. absent optional fields).
 */
const toTilePost = (post: Immutable<IPost>): IUGCPost => ({
    title: post.title,
    post_type: post.post_type,
    pull_quote: post.pull_quote,
    copy: post.copy,
    social_media: post.social_media,
    username: post.username,
    avatar: post.avatar === null ? undefined : isoImageURL.wrap(post.avatar),
    avatar_title: post.avatar_title ?? undefined,
    photo: post.photo === null ? undefined : isoImageURL.wrap(post.photo),
    photo_title: post.photo_title ?? undefined,
    social_link: post.social_link,
    video_link: post.video_link ?? undefined,
    view_post: post.view_post,
    recommendations: post.recommendations.map((rec) => ({
        title: rec.title,
        url: rec.url,
        image_url: isoImageURL.wrap(rec.image_url),
        rating: rec.rating,
        num_reviews: rec.num_reviews,
    })),
});

const toTiles = (posts: IProps["value"]["posts"]): IUGCTile[] => {
    const tiles: IUGCTile[] = [];
    for (const item of posts ?? []) {
        // A null post means the chosen UGCPost snippet was deleted. The
        // legacy grid rendered nothing for those tiles (`buildTile` returns
        // null), so dropping them here produces identical DOM while keeping
        // the io-ts `post` field non-null. Grid CTA anchors and gallery slide
        // ids both derive from this same filtered array, so they stay in sync.
        if (item.post) {
            tiles.push({ post: toTilePost(item.post) });
        }
    }
    return tiles;
};

/**
 * Native-reactivated port of the sealy UGCGrid block. Reproduces the legacy
 * wrapper DOM exactly and
 * reuses the shared `UGCGrid` post-grid component (previously mounted via the
 * nested `tsicommon.cms.templates.UGCGridTemplate` include_react island).
 *
 * `desc` is a tsicommon RawHTMLBlock value: the serialized string already
 * includes the `<div class="raw-html-block">` wrapper (it is `str(RawHTML)`,
 * exactly what legacy `{{ value.desc }}` emitted), so it is injected verbatim
 * with nothing added.
 */
export const SealyUGCGrid = (props: IProps): React.JSX.Element => {
    const v = props.value;
    return (
        <div className="ugc-grid l-capped-width">
            <div className="ugc-grid__header">
                <h2 className="ugc-grid__title">{v.title}</h2>
                <div
                    className="ugc-grid__desc"
                    dangerouslySetInnerHTML={{ __html: v.desc ?? "" }}
                />
            </div>
            <UGCPostGrid tiles={toTiles(v.posts)} />
            {v.cta && (
                <a className="ugc-grid__cta" href={v.cta.url}>
                    {t`View More`}
                </a>
            )}
        </div>
    );
};
